import { ReactNode, useState, useMemo } from "react";
import queryString from "query-string";
import useSWR from "swr";
import yn from "yn";
import EmbedContainer from "~/components/EmbedContainer";
import EmbedOverlay from "~/components/EmbedOverlay";
import { ACCESS_CONTROLLER_V1 } from "~/utils/accessController";
import { apiClient, apiMutate } from "~/utils/apiClient";
import monitoring from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_SIGMA_URL, VITE_SIGMA_SELF_SERVICE_ENABLED } = import.meta.env;

const getSigmaSourceUrl = (workbookUrl: string): string | null => {
  try {
    const url = new URL(workbookUrl);
    let sourceUrl = `${url.origin}${url.pathname}`;
    const searchParams = new URLSearchParams(url.search);
    const selectedNode = url.searchParams.get(":nodeId");
    if (selectedNode && !sourceUrl.includes("/edit") && !sourceUrl.includes("/page/")) {
      // Page element is selected in url using query string instead of context path, reformat for request
      sourceUrl += `/page/${selectedNode}`;
      // Remove nodeId query param since we're loading the workbook as a single page
      searchParams.delete(":nodeId");
    }

    // Add remaining query parameters to the sourceUrl
    const remainingParams = searchParams.toString();
    if (remainingParams) {
      sourceUrl += `?${remainingParams}`;
    }

    return sourceUrl;
  } catch (e) {
    console.error(e);
    monitoring?.recordError(e);
  }
  return null;
};

const getCatalogDashboardUrl = (datasetId: string, reportFqn: string): string | null => {
  try {
    if (!datasetId || !reportFqn) {
      return null;
    }
    return `${VITE_CATALOG_API}/datasets/${datasetId}/reports/${reportFqn}/embed-url`;
  } catch (e) {
    console.error(e);
    monitoring?.recordError(e);
  }
  return null;
};

const getEmbedUrl = (options: {
  workbookUrl?: string;
  datasetId?: string;
  reportFqn?: string;
}): string | null => {
  const { workbookUrl, datasetId, reportFqn } = options;

  if (workbookUrl) {
    return getSigmaSourceUrl(workbookUrl);
  } else if (datasetId && reportFqn) {
    return getCatalogDashboardUrl(datasetId, reportFqn);
  }

  return null;
};

interface SigmaEmbed {
  title?: ReactNode;
  workbookUrl?: string;
  datasetId?: string;
  reportFqn?: string;
  inline?: boolean;
  onClose?: () => void;
  messageHandler?: (event: MessageEvent) => void;
  allowEdits?: boolean;
}

/**
 * Modifies the source URL based on user permissions
 * - Always modifies to remove /edit and open in view mode
 * - If user has edit:catalog-report permission, adds necessary query parameters to populate menu
 */
const getModifiedSourceUrl = (sourceUrl: string, hasEditPermission: boolean): string => {
  // Parse the URL to handle existing query parameters and check if path already ends with /edit
  const url = new URL(sourceUrl);

  // Remove /edit if it exists
  if (url.pathname.endsWith("/edit")) {
    url.pathname = url.pathname.slice(0, -5);
  }

  // If have edit permissions - modify edit menu based query params
  if (yn(VITE_SIGMA_SELF_SERVICE_ENABLED) && hasEditPermission) {
    // Add editing query parameters to any existing ones
    const newParams = {
      [":hide_menu"]: true,
      [":hide_folder_navigation"]: true,
      [":menu_position"]: "bottom",
    };

    // Parse existing query string
    const existingParams = queryString.parse(url.search);

    // Combine existing and new parameters
    const combinedParams = { ...existingParams, ...newParams };

    // Set the new search string
    url.search = queryString.stringify(combinedParams);
  }

  return url.toString();
};

const SigmaEmbed = ({
  title,
  workbookUrl,
  datasetId,
  reportFqn,
  inline,
  onClose,
  messageHandler,
  allowEdits = false,
}: SigmaEmbed) => {
  const [loading, setLoading] = useState(true);
  const refreshInterval = 24 * 60 * 60 * 1000;
  const leeway = 5 * 1000;
  const embedUrlKey = getEmbedUrl({ workbookUrl, datasetId, reportFqn });
  const {
    data: sigmaEmbedUrl,
    error,
    isLoading,
  } = useSWR<{ url: string }>(
    embedUrlKey,
    (key: string) =>
      workbookUrl
        ? apiMutate<{ url: string }>(`${ACCESS_CONTROLLER_V1}/users/me/sigma-embed-urls`, "POST", {
            source_url: key,
          })
        : apiClient<{ url: string }>(key),
    {
      // Refresh every 24 hours (with 5 seconds of leeway)
      refreshInterval: refreshInterval - leeway,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
    }
  );

  // Parse the error message from the API
  const errorMessage = useMemo(() => {
    if (!error) {
      // Only surface a "failed" error when we had a real key to fetch — a null key means
      // the caller hasn't supplied enough data yet, so stay in loading state instead.
      if (!isLoading && !sigmaEmbedUrl && embedUrlKey !== null) {
        return "Failed to load dashboard URL";
      }
      return null;
    }

    try {
      const parsedError = JSON.parse(error.message);
      if (parsedError.detail) {
        return parsedError.detail;
      }
    } catch {
      // error.message wasn't JSON
    }

    if (error.status === 403) {
      return "You don't have permission to access this dashboard";
    }
    if (error.status === 404) {
      return "Dashboard not found";
    }

    return "Failed to load dashboard";
  }, [error, isLoading, sigmaEmbedUrl, embedUrlKey]);

  const editableSigmaEmbedUrl = sigmaEmbedUrl
    ? getModifiedSourceUrl(sigmaEmbedUrl.url, allowEdits)
    : undefined;

  const handleLoadingEvent = (e: MessageEvent) => {
    if (
      e.origin === VITE_SIGMA_URL &&
      ["workbook:loaded", "workbook:error"].includes(e.data.type)
    ) {
      setLoading(false);
    }
  };

  // Determine overall loading state: show loading while fetching URL or while iframe is loading
  const isLoadingOverall = isLoading || (loading && !errorMessage);

  const props = {
    key: workbookUrl || `${datasetId}-${reportFqn}`,
    title,
    loading: isLoadingOverall,
    iframeSrc: editableSigmaEmbedUrl,
    error: errorMessage,
    messageHandler: (e: MessageEvent) => {
      handleLoadingEvent(e);
      if (messageHandler) {
        messageHandler(e);
      }
    },
  };

  if (inline) {
    return <EmbedContainer {...props} />;
  }

  if (!onClose) {
    throw Error("onClose handler required for Sigma overlay modal");
  }

  return <EmbedOverlay hideOpenInNewTab onClose={onClose} {...props} />;
};

export default SigmaEmbed;
